import { Fragment, type ReactNode } from 'react';

import { Badge } from '@playbook/atoms/badge';
import { TableCell } from '@playbook/atoms/table-cell';
import { TableHeaderCell } from '@playbook/atoms/table-header-cell';
import { TableRow } from '@playbook/atoms/table-row';
import { Table } from '@playbook/compositions/table';

import { useKeyEvents } from '@portal/pkg/api/keys';
import type { KeyEvent } from '@portal/pkg/api/types';
import { formatDate, formatDateTime } from '@portal/views/keys/_shared';

import css from './KeyActivity.module.css';

const OperationLabels: Record<string, string> = {
	'papi-key-created': 'Created',
	'papi-key-rotated': 'Created by rotation',
	'papi-key-revoked': 'Revoked',
	'papi-key-grace-shortened': 'Retired early',
	'papi-key-capabilities-changed': 'Access changed',
};

export function KeyActivity({ id }: { id: string }): ReactNode {
	const events = useKeyEvents(id);

	if (events.length === 0) {
		return <p className={css.quiet}>Nothing recorded for this key yet.</p>;
	}

	return (
		<Table
			size="small"
			label="What has happened to this key"
			// Cells, not a row: Table wraps these in the header row itself.
			header={
				<Fragment>
					<TableHeaderCell type="lead" label="What" />
					<TableHeaderCell label="Who" />
					<TableHeaderCell label="When" />
				</Fragment>
			}>
			{events.map((event) => (
				<TableRow key={event.id}>
					<TableCell type="lead" label={label(event)} detail={detail(event)} />
					<TableCell
						content={
							event.actor ? (
								event.actor.name
							) : (
								<Badge variant="information" label="the key itself" />
							)
						}
					/>
					<TableCell label={formatDateTime(event.occurredAt)} />
				</TableRow>
			))}
		</Table>
	);
}

function label(event: KeyEvent): string {
	return OperationLabels[event.operation] ?? fallbackLabel(event);
}

/**
 *	Events written before each change had a name of its own carry an empty
 *	operation, so the event type is all there is to go on.
 */
function fallbackLabel(event: KeyEvent): string {
	return event.eventType === 'created' ? 'Created' : 'Changed';
}

/** What actually moved, in the words the rest of the portal uses. */
function detail(event: KeyEvent): Optional<string> {
	const changes = Object.entries(event.changes);

	if (changes.length === 0) {
		return undefined;
	}

	return changes
		.map(([field, change]) => describeChange(field, change.from, change.to))
		.join(', ');
}

function describeChange(field: string, from: unknown, to: unknown): string {
	switch (field) {
		case 'capabilities':
			return describeCapabilities(asStrings(from), asStrings(to));
		case 'expiresAt':
			// A key older than the event log has no earlier snapshot, so the
			// server reports the new value alone rather than a move.
			return from === null || from === undefined
				? `expires ${formatDate(Number(to))}`
				: `expiry ${formatDate(Number(from))} → ${formatDate(Number(to))}`;
		case 'revokedAt':
			return 'key revoked';
		case 'name':
			return `renamed from “${String(from)}”`;
		default:
			return field;
	}
}

function describeCapabilities(before: string[], after: string[]): string {
	const added = after.filter((capability) => !before.includes(capability));
	const removed = before.filter((capability) => !after.includes(capability));

	const parts: string[] = [];

	if (added.length > 0) {
		parts.push(`added ${added.join(', ')}`);
	}

	if (removed.length > 0) {
		parts.push(`removed ${removed.join(', ')}`);
	}

	return parts.join(' · ');
}

function asStrings(value: unknown): string[] {
	return Array.isArray(value) ? value.map(String) : [];
}
